<template>
  <el-dialog
    :title="item ? '编辑词条' : '新增词条'"
    :visible="visible"
    :close-on-click-modal="!saving"
    :close-on-press-escape="!saving"
    :show-close="!saving"
    @close="close"
  >
    <el-form v-loading="platformLoading" :model="form" label-width="110px" :disabled="saving || platformLoading">
      <el-form-item label="类型">
        <el-select v-model="form.term_type" @change="handleTypeChange">
          <el-option label="品牌词" value="brand" />
          <el-option label="产品/SKU" value="sku" />
          <el-option label="风险词" value="abnormal" />
        </el-select>
      </el-form-item>
      <el-form-item label="词条">
        <el-input v-model="form.term" />
      </el-form-item>
      <el-form-item v-if="riskTerm" label="风险分类">
        <el-select v-model="form.category" placeholder="请选择风险分类">
          <el-option v-for="category in riskCategories" :key="category" :label="category" :value="category" />
        </el-select>
        <p class="hint">用于风险中心归类和同类风险合并。</p>
      </el-form-item>
      <el-form-item v-if="riskTerm" label="风险等级">
        <el-select v-model="form.risk_level">
          <el-option v-for="value in ['低', '中', '高']" :key="value" :value="value" />
        </el-select>
      </el-form-item>
      <el-form-item :label="riskTerm ? '启用风险识别' : '启用'">
        <el-switch v-model="form.active" />
        <p v-if="riskTerm" class="hint">启用后，将对已采集的公开内容和评论进行风险识别。</p>
      </el-form-item>
      <template v-if="searchable">
        <el-form-item label="参与自动采集">
          <el-switch v-model="form.search_enabled" @change="handleSearchEnabled" />
        </el-form-item>
        <el-form-item v-if="form.search_enabled" label="检索平台">
          <el-checkbox-group v-model="form.platforms">
            <el-checkbox
              v-for="platform in readyPlatforms"
              :key="platform.platform"
              :label="platform.platform"
            >
              {{ platform.display_name }}
            </el-checkbox>
          </el-checkbox-group>
          <p v-if="!readyPlatforms.length" class="hint warning">当前没有验证通过的平台，请先到系统管理完成平台验证。</p>
          <p v-else class="hint">已自动选择验证通过的平台，可按实际需要调整。</p>
        </el-form-item>
        <el-form-item v-if="form.search_enabled" label="搜索优先级">
          <el-input-number v-model="form.search_priority" :min="0" :max="999" />
          <p class="hint">数值越大，自动搜索优先级越高。</p>
        </el-form-item>
      </template>
    </el-form>
    <span slot="footer">
      <el-button :disabled="saving" @click="close">取消</el-button>
      <el-button type="primary" :loading="saving" :disabled="platformLoading" @click="save">{{saving?'保存中':'保存'}}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'

const empty = () => ({
  term_type: 'brand',
  term: '',
  category: '其他问题',
  risk_level: '低',
  active: true,
  search_enabled: true,
  search_priority: 50,
  platforms: []
})

export default {
  props: ['visible', 'item'],
  data () {
    return {
      form: empty(),
      platforms: [],
      platformLoading: false,
      saving: false,
      riskCategories: ['产品质量', '健康安全', '服务售后', '价格促销', '物流配送', '其他问题']
    }
  },
  computed: {
    searchable () {
      return ['brand', 'sku'].includes(this.form.term_type)
    },
    riskTerm () {
      return this.form.term_type === 'abnormal'
    },
    readyPlatforms () {
      return this.platforms.filter(platform => platform.search_ready)
    }
  },
  watch: {
    visible (visible) {
      if (!visible) return
      this.form = this.item
        ? { ...this.item, platforms: [...(this.item.platforms || [])] }
        : empty()
      this.normalizeRiskCategory()
      this.loadPlatforms()
    }
  },
  methods: {
    async loadPlatforms () {
      this.platformLoading = true
      try {
        this.platforms = (await yuqingApi.get('platform-readiness')).readiness || []
        this.selectReadyPlatforms()
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.platformLoading = false
      }
    },
    selectReadyPlatforms () {
      if (!this.searchable || !this.form.search_enabled || this.form.platforms.length) return
      this.form.platforms = this.readyPlatforms.map(platform => platform.platform)
    },
    handleTypeChange () {
      if (!this.searchable) {
        this.form.search_enabled = false
        this.form.search_priority = 0
        this.form.platforms = []
        this.normalizeRiskCategory()
        return
      }
      this.form.search_enabled = true
      if (!this.form.search_priority) this.form.search_priority = 50
      this.selectReadyPlatforms()
    },
    normalizeRiskCategory () {
      if (!this.riskTerm) return
      const legacy = { '口感/变质': '产品质量', '品质': '产品质量', '身体不适': '健康安全', '客服/投诉': '服务售后', '价格/促销': '价格促销', '物流/配送': '物流配送', '其他': '其他问题' }
      this.form.category = legacy[this.form.category] || (this.riskCategories.includes(this.form.category) ? this.form.category : '其他问题')
    },
    handleSearchEnabled (enabled) {
      if (enabled) this.selectReadyPlatforms()
    },
    async save () {
      if (this.saving || this.platformLoading) return
      if (!this.form.term.trim()) return this.$message.warning('请输入词条')
      const payload = {
        ...this.form,
        platforms: [...this.form.platforms]
      }
      if (!this.searchable) {
        payload.search_enabled = false
        payload.search_priority = 0
        payload.platforms = []
      } else {
        const ready = new Set(this.readyPlatforms.map(platform => platform.platform))
        payload.platforms = payload.platforms.filter(platform => ready.has(platform))
      }
      if (this.searchable && payload.search_enabled && !payload.platforms.length) {
        return this.$message.warning('请选择至少一个验证通过的检索平台')
      }
      this.saving = true
      try {
        if (this.item) await yuqingApi.put(`lexicon/${this.item.id}`, payload)
        else await yuqingApi.post('lexicon', payload)
        this.$message.success('词条已保存')
        this.saving = false
        this.close()
        this.$emit('success')
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.saving = false
      }
    },
    close () { if (!this.saving) this.$emit('update:visible', false) }
  }
}
</script>

<style scoped>
.hint {
  margin: 6px 0 0;
  color: #909399;
  font-size: 12px;
}
.warning {
  color: #e6a23c;
}
</style>
